import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, User, Phone, Mail, MessageSquare, Check, ChevronDown } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import { getLocalDateString, getTimeSlotDisplayItems } from '../../../utils/dateUtils';
import './ReservarCitaModal.css';

// Helpers de validación de teléfono colombiano (inline para evitar problemas de módulo)
function normalizeColombiaPhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.startsWith('57') && digits.length >= 12) return digits.slice(2, 12);
    if (digits.startsWith('57') && digits.length === 11) return digits.slice(2);
    return digits;
}
function isValidColombiaMobile10(digits) {
    return /^3\d{9}$/.test(digits);
}
const CO_PHONE_ERROR_MSG = 'Ingresa un celular colombiano válido: 10 dígitos sin +57 (ejemplo: 3154448492).';

const DROPDOWN_MAX_H = 280;

const ReservarCitaModal = ({ barbero, service, isOpen, onClose, clientData, onSuccess, embedded }) => {
    // Inicializar con el servicio seleccionado si está disponible
    const [formData, setFormData] = useState({
        nombre: '',
        telefono: '',
        email: '',
        fecha: '',
        hora: '',
        servicio: service ? service.name : '',
        notas: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [horarios, setHorarios] = useState([]);
    const [isLoadingHorarios, setIsLoadingHorarios] = useState(false);
    /** Días de la semana cerrados según shop_schedule: Set de números JS (0=Dom, 1=Lun, …, 6=Sáb). */
    const [closedDaysJS, setClosedDaysJS] = useState(new Set());
    const [closedDayNames, setClosedDayNames] = useState([]);
    const [dateClosedWarning, setDateClosedWarning] = useState('');
    const [duracionServicio, setDuracionServicio] = useState(
        service ? (service.duration_minutes || 30) : 30
    );
    const [selectedTime24, setSelectedTime24] = useState('');
    const [timePickerOpen, setTimePickerOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const timePickerRef = useRef(null);
    const timePickerTriggerRef = useRef(null);
    const availabilityRequestIdRef = useRef(0);

    // Cerrar selector de hora al hacer clic fuera (el dropdown está en portal, no dentro del ref)
    useEffect(() => {
        const handleClickOutside = (e) => {
            const inTrigger = timePickerRef.current && timePickerRef.current.contains(e.target);
            const inDropdown = e.target.closest('.time-picker-dropdown-portal');
            if (!inTrigger && !inDropdown) setTimePickerOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    // Cargar horario semanal para bloquear días cerrados
    React.useEffect(() => {
        const fetchSchedule = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/barber/schedule`);
                if (!res.ok) return;
                const data = await res.json();
                const weekly = Array.isArray(data) ? data : (data.weekly || []);
                // shop_schedule: day_of_week usa Python (0=Lun…6=Dom); JS date.getDay(): 0=Dom,1=Lun…6=Sáb
                // Convertir: Python dow → JS dow: (python_dow + 1) % 7
                const closedJS = new Set();
                const closedNames = [];
                const dayNamesES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
                weekly.forEach(d => {
                    if (!d.enabled) {
                        const jsDow = (d.day_of_week + 1) % 7;
                        closedJS.add(jsDow);
                        closedNames.push(d.day_name || dayNamesES[d.day_of_week] || '');
                    }
                });
                setClosedDaysJS(closedJS);
                setClosedDayNames(closedNames);
            } catch (e) {
                console.error('Error cargando horario semanal:', e);
            }
        };
        fetchSchedule();
    }, []);

    // Pre-llenar con datos del cliente si está logueado
    React.useEffect(() => {
        if (isOpen && clientData) {
            setFormData(prev => ({
                ...prev,
                nombre: clientData.nombre || prev.nombre,
                telefono: clientData.telefono || prev.telefono,
                email: clientData.email || prev.email
            }));
        }
    }, [isOpen, clientData]);

    // Actualizar cuando cambie el servicio
    React.useEffect(() => {
        if (service) {
            setFormData(prev => ({
                ...prev,
                servicio: service.name
            }));
            setDuracionServicio(service.duration_minutes || 30);
        }
    }, [service]);

    // Cargar disponibilidad cuando cambia la fecha O la duración del servicio
    React.useEffect(() => {
        if (!formData.fecha || !barbero) return;

        // Identificador de esta petición: si llega una respuesta de una petición
        // anterior (más lenta) después de una más reciente, se descarta para
        // evitar pisar los horarios de la fecha actual con datos obsoletos.
        const requestId = ++availabilityRequestIdRef.current;
        const isStale = () => requestId !== availabilityRequestIdRef.current;

        const fetchAvailability = async () => {
            setIsLoadingHorarios(true);
            setTimeWarning(null);
            try {
                const duracion = service ? (service.duration_minutes || 30) : duracionServicio;
                const response = await fetch(`${API_BASE_URL}/barber/availability?barber_id=${barbero.id}&date_str=${formData.fecha}&duration=${duracion}`);
                if (isStale()) return;

                if (response.ok) {
                    const data = await response.json();
                    if (isStale()) return;
                    setHorarios(Array.isArray(data) ? data : []);

                    // Si ya había una hora seleccionada, verificar si sigue siendo válida
                    if (selectedTime24) {
                        const selectedSlot = data.find(s => s.time_24 === selectedTime24);
                        if (!selectedSlot || !selectedSlot.available) {
                            setSelectedTime24('');
                            setFormData(prev => ({ ...prev, hora: '' }));
                            setTimeWarning(
                                `El horario que tenías seleccionado no tiene tiempo suficiente para completar el servicio (${duracion} min). Por favor elige otro horario.`
                            );
                        }
                    }
                } else {
                    setHorarios([]);
                    setTimeWarning('No se pudieron cargar los horarios disponibles. Intenta seleccionar la fecha de nuevo.');
                }
            } catch (error) {
                console.error("Error fetching availability:", error);
                if (!isStale()) {
                    setHorarios([]);
                    setTimeWarning('No se pudieron cargar los horarios disponibles. Verifica tu conexión e intenta de nuevo.');
                }
            } finally {
                if (!isStale()) setIsLoadingHorarios(false);
            }
        };

        fetchAvailability();
    }, [formData.fecha, barbero, duracionServicio, service]); // eslint-disable-line react-hooks/exhaustive-deps

    // Si el servicio viene como prop, no permitir cambiarlo
    // Si no viene, mantener la funcionalidad anterior (por compatibilidad)
    const handleServiceChange = (e) => {
        if (service) {
            // Si hay un servicio seleccionado, no permitir cambiarlo
            return;
        }
        const selectedName = e.target.value;
        setFormData(prev => ({
            ...prev,
            servicio: selectedName
        }));
    };

    const horariosDisponibles = [
        // Fallback or legacy (not used if dynamic works)
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'fecha' && value) {
            // Validar que el día seleccionado no sea un día cerrado
            // new Date('YYYY-MM-DD') en UTC, .getUTCDay() evita desfases de zona horaria
            const d = new Date(value + 'T00:00:00');
            const jsDow = d.getDay(); // 0=Dom,1=Lun…6=Sáb (hora local)
            if (closedDaysJS.has(jsDow)) {
                const names = closedDayNames.length > 0 ? closedDayNames.join(', ') : 'ese día';
                setDateClosedWarning(`La barbería no atiende los días ${names}. Por favor elige otra fecha.`);
                // No actualizar la fecha en formData, dejar el campo limpio
                setFormData(prev => ({ ...prev, fecha: '', hora: '' }));
                setSelectedTime24('');
                setHorarios([]);
                return;
            } else {
                setDateClosedWarning('');
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const [error, setError] = useState(null);
    const [timeWarning, setTimeWarning] = useState(null);
    const errorAlertRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        const phoneDigits = normalizeColombiaPhone(formData.telefono);
        if (!isValidColombiaMobile10(phoneDigits)) {
            setError(CO_PHONE_ERROR_MSG);
            setIsSubmitting(false);
            setTimeout(() => errorAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${API_BASE_URL}/barber/book-appointment`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    client_name: formData.nombre,
                    client_phone: phoneDigits,
                    client_email: formData.email,
                    barber_id: barbero.id,
                    appointment_date: formData.fecha,
                    appointment_time: selectedTime24 || formData.hora,
                    service_name: formData.servicio,
                    service_id: service?.id || null,
                    price: service?.price ?? null,
                    notes: formData.notas
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const message = Array.isArray(errorData.detail)
                    ? errorData.detail.map((d) => (d.msg != null ? d.msg : d)).join(', ')
                    : (errorData.detail || 'Error al procesar la reserva');
                throw new Error(message);
            }

            const data = await response.json();

            setIsSubmitting(false);
            setIsSuccess(true);
            if (typeof onSuccess === 'function') onSuccess();

            // Cerrar modal después de mostrar éxito
            setTimeout(() => {
                setIsSuccess(false);
                onClose();
                // Resetear el formulario
                setFormData({
                    nombre: '',
                    telefono: '',
                    email: '',
                    fecha: '',
                    hora: '',
                    servicio: '',
                    notas: ''
                });
            }, 3000);

        } catch (err) {
            console.error('Error al reservar:', err);
            setError(err.message);
            setIsSubmitting(false);
            setTimeout(() => errorAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!barbero) return null;

    const content = (
        <>
            {/* Modal Header - ocultar si está embebido */}
            {!embedded && (
                <div className="modal-header">
                    <div className="modal-header-content">
                        <h2>Reservar cita</h2>
                        <p>con {barbero.nombre}</p>
                    </div>
                    <button className="modal-close-btn" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>
            )}
            {embedded && (
                <div className="modal-header modal-header-embedded">
                    <h2>Reservar cita con {barbero.nombre}</h2>
                </div>
            )}

            {/* Success State */}
            {isSuccess ? (
                            <div className="success-state">
                                <div className="success-icon">
                                    <Check size={48} />
                                </div>
                                <h3>¡Cita agendada exitosamente!</h3>
                                <p>Se envió la confirmación de tu cita a tu correo electrónico</p>
                            </div>
                        ) : (
                            <>
                                {/* Barbero Info */}
                                <div className="modal-barbero-info">
                                    <div className="modal-barbero-details">
                                        <p className="modal-especialidad">{barbero.especialidad}</p>
                                        <p className="modal-experiencia">
                                            <Clock size={16} />
                                            {barbero.experiencia} de experiencia
                                        </p>
                                    </div>
                                </div>

                                {/* Error Alert / Toast: horario no disponible, etc. */}
                                {timeWarning && (
                                    <div className="time-warning-alert" role="alert">
                                        <span className="time-warning-icon">⚠️</span>
                                        <p>{timeWarning}</p>
                                    </div>
                                )}
                                {error && (
                                    <div className="error-alert" ref={errorAlertRef} role="alert">
                                        <p>{error}</p>
                                    </div>
                                )}

                                {/* Form */}
                                <form className="modal-form" onSubmit={handleSubmit}>
                                    {/* Datos Personales */}
                                    <div className="form-section">
                                        <h3 className="form-section-title">
                                            <User size={20} />
                                            Datos personales
                                        </h3>
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label htmlFor="nombre">Nombre completo *</label>
                                                <input
                                                    type="text"
                                                    id="nombre"
                                                    name="nombre"
                                                    value={formData.nombre}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder="Juan Pérez"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label htmlFor="telefono">Teléfono *</label>
                                                <input
                                                    type="tel"
                                                    id="telefono"
                                                    name="telefono"
                                                    value={formData.telefono}
                                                    onChange={handleInputChange}
                                                    required
                                                    inputMode="numeric"
                                                    autoComplete="tel"
                                                    placeholder="3154448492"
                                                    title="10 dígitos, celular Colombia (empieza en 3)"
                                                />
                                                <p className="form-hint-phone" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', marginBottom: 0 }}>
                                                    Obligatorio. Celular colombiano: 10 dígitos sin +57 (ej. 3154448492).
                                                </p>
                                            </div>
                                            <div className="form-group full-width">
                                                <label htmlFor="email">Email</label>
                                                <input
                                                    type="email"
                                                    id="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    placeholder="correo@ejemplo.com"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Detalles de la Cita */}
                                    <div className="form-section">
                                        <h3 className="form-section-title">
                                            <Calendar size={20} />
                                            Detalles de la cita
                                        </h3>
                                        {dateClosedWarning && (
                                            <div className="date-closed-warning">
                                                🚫 {dateClosedWarning}
                                            </div>
                                        )}
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label htmlFor="fecha">Fecha *</label>
                                                <input
                                                    type="date"
                                                    id="fecha"
                                                    name="fecha"
                                                    value={formData.fecha}
                                                    onChange={handleInputChange}
                                                    required
                                                    min={getLocalDateString()}
                                                />
                                            </div>
                                            <div className="form-group time-picker-wrapper" ref={timePickerRef}>
                                                <label htmlFor="hora">Hora *</label>
                                                <button
                                                    ref={timePickerTriggerRef}
                                                    type="button"
                                                    id="hora"
                                                    className="time-picker-trigger"
                                                    onClick={() => {
                                                        if (isLoadingHorarios || !formData.fecha) return;
                                                        if (!timePickerOpen && timePickerTriggerRef.current) {
                                                            const rect = timePickerTriggerRef.current.getBoundingClientRect();
                                                            const vh = window.innerHeight;
                                                            const vw = window.innerWidth;
                                                            const spaceBelow = vh - rect.bottom - 8;
                                                            const openAbove = spaceBelow < DROPDOWN_MAX_H;
                                                            const top = openAbove
                                                                ? Math.max(8, rect.top - DROPDOWN_MAX_H - 4)
                                                                : rect.bottom + 4;
                                                            const width = Math.min(rect.width, vw - 16);
                                                            const left = Math.max(8, Math.min(rect.left, vw - width - 8));
                                                            setDropdownPosition({ top, left, width });
                                                        }
                                                        setTimePickerOpen(prev => !prev);
                                                    }}
                                                    disabled={isLoadingHorarios || !formData.fecha}
                                                    aria-expanded={timePickerOpen}
                                                    aria-haspopup="listbox"
                                                    aria-label="Seleccionar hora"
                                                >
                                                    <span className="time-picker-value">
                                                        {formData.hora || (isLoadingHorarios ? 'Cargando horarios...' : 'Seleccionar hora')}
                                                    </span>
                                                    <ChevronDown size={18} className={timePickerOpen ? 'time-picker-chevron-open' : ''} />
                                                </button>
                                                <input
                                                    type="hidden"
                                                    name="hora"
                                                    value={formData.hora}
                                                    required
                                                    tabIndex={-1}
                                                    aria-hidden
                                                />
                                                {timePickerOpen && createPortal(
                                                    <AnimatePresence>
                                                        <motion.div
                                                            className="time-picker-dropdown time-picker-dropdown-portal"
                                                            style={{
                                                                position: 'fixed',
                                                                top: dropdownPosition.top,
                                                                left: dropdownPosition.left,
                                                                width: dropdownPosition.width,
                                                                zIndex: 99999
                                                            }}
                                                            initial={{ opacity: 0, y: -8 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            exit={{ opacity: 0, y: -8 }}
                                                            transition={{ duration: 0.2 }}
                                                            role="listbox"
                                                            aria-label="Horarios disponibles"
                                                        >
                                                            <div className="time-picker-dropdown-inner">
                                                                <div className="time-picker-dropdown-title">Seleccionar hora</div>
                                                                {getTimeSlotDisplayItems(horarios, 5).length === 0 ? (
                                                                    <div className="time-picker-option time-picker-empty">
                                                                        No hay horarios disponibles para esta fecha. Intenta con otra fecha o vuelve a seleccionar la misma fecha para recargar.
                                                                    </div>
                                                                ) : (
                                                                    getTimeSlotDisplayItems(horarios, 5).map((item, index) =>
                                                                        item.type === 'available' ? (
                                                                            <button
                                                                                key={index}
                                                                                type="button"
                                                                                role="option"
                                                                                className="time-picker-option time-picker-option-available"
                                                                                onClick={() => {
                                                                                    setFormData(prev => ({ ...prev, hora: item.slot.time_display }));
                                                                                    setSelectedTime24(item.slot.time_24);
                                                                                    setTimeWarning(null);
                                                                                    setTimePickerOpen(false);
                                                                                }}
                                                                            >
                                                                                {item.slot.time_display} ✓ Disponible
                                                                            </button>
                                                                        ) : (
                                                                            <div key={index} className={`time-picker-option time-picker-option-unavailable${item.label === 'Sin tiempo suficiente' ? ' time-picker-option-no-time' : ''}`}>
                                                                                {item.startDisplay} - {item.endDisplay} · {item.label || 'Ocupado'}
                                                                            </div>
                                                                        )
                                                                    )
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    </AnimatePresence>,
                                                    document.body
                                                )}
                                                {formData.fecha && horarios.length > 0 && (
                                                    <p style={{ 
                                                        fontSize: '0.75rem', 
                                                        color: '#6b7280', 
                                                        marginTop: '0.25rem',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        {horarios.filter(s => s.available).length} horarios disponibles
                                                    </p>
                                                )}
                                            </div>
                                            <div className="form-group full-width">
                                                <label htmlFor="servicio">Servicio *</label>
                                                {service ? (
                                                    <div className="service-selected">
                                                        <div className="service-selected-info">
                                                            <span className="service-name">{service.name}</span>
                                                            {service.price && (
                                                                <span className="service-price">
                                                                    {new Intl.NumberFormat('es-CO', {
                                                                        style: 'currency',
                                                                        currency: 'COP',
                                                                        minimumFractionDigits: 0
                                                                    }).format(service.price)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <input
                                                            type="hidden"
                                                            name="servicio"
                                                            value={service.name}
                                                        />
                                                    </div>
                                                ) : (
                                                    <select
                                                        id="servicio"
                                                        name="servicio"
                                                        value={formData.servicio}
                                                        onChange={handleServiceChange}
                                                        required
                                                    >
                                                        <option value="">Seleccionar servicio</option>
                                                    </select>
                                                )}
                                            </div>
                                            <div className="form-group full-width">
                                                <label htmlFor="notas">
                                                    <MessageSquare size={16} />
                                                    Notas adicionales (opcional)
                                                </label>
                                                <textarea
                                                    id="notas"
                                                    name="notas"
                                                    value={formData.notas}
                                                    onChange={handleInputChange}
                                                    rows="3"
                                                    placeholder="Alguna preferencia o detalle que debamos conocer..."
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="modal-actions">
                                        <button
                                            type="button"
                                            className="btn-cancel"
                                            onClick={onClose}
                                            disabled={isSubmitting}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn-submit"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <div className="btn-spinner"></div>
                                                    <span>Procesando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Calendar size={20} />
                                                    <span>Confirmar reserva</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </>
                        )}
                    </>
    );

    if (embedded) {
        return isOpen ? <div className="reservar-cita-embedded">{content}</div> : null;
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={handleBackdropClick}
                >
                    <motion.div
                        className="modal-container"
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {content}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ReservarCitaModal;
